import {
  SuperTable,
  getParsedCrudData,
  getTranslatedText,
  useIntl,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components/src';
import {
  AccountClient,
  useQuery,
  GET_TECH_GROUP,
  GET_TECHS_BY_CRITERIA,
  useCachedQuery
} from '@nile/graphql';
import {
  Avatar,
  Page,
  TextStyle,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { cloneDeep, isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import SchedulingContext from '../Contexts/SchedulingContext';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';

const TechnicianOrTechGroupCell = (props) => {
  const data = props?.cell?.row?.original;
  const intl = useIntl();

  if (data?.userId) {
    const calendarNotIntegrated = !data?.calendarIntegrated;
    return (
      <React.Fragment key={data?.userId}>
        <div className="flex g10 actr pointer-events-all">
          <div>
            <Avatar size="small" initials={data?.name?.[0]} />
          </div>
          <div>
            <TextStyle>{data?.name}</TextStyle>
          </div>
          {calendarNotIntegrated && (
            <Tooltip
              title={getTranslatedText(
                intl,
                'scheduling.technician.calendar.integration.pending.info'
              )}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
              arrow
              distance={5}
            >
              <div className="pointer pt-5">
                <SVGIcon name="About" />
              </div>
            </Tooltip>
          )}
        </div>
      </React.Fragment>
    );
  }

  if (data?.groupId) {
    return (
      <React.Fragment key={data?.groupId}>
        <TextStyle weight="SemiBold">
          {data?.name} {`(${data?.groupSize || '0'})`}
        </TextStyle>
      </React.Fragment>
    );
  }

  return <></>;
};

const SelectTechnicianContainer = () => {
  const intl = useIntl();
  const schedulingContext = useContext(SchedulingContext);
  const [tableData, setTableData] = useState([]);
  const productType = useProduct();
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'name', columns: [] }
  ]);

  const [records, setRecords] = useState([]);
  const [primaryActionLoading, setPrimaryActionLoading] = useState(false);

  const isRowDisabled = (row) => {
    const allData = cloneDeep(tableData);
    const data = row?.original;
    if (data?.type === 'group') {
      const assosiatedTechs = allData.filter(
        (el) => el?.groupId === data?.groupId && el?.type === 'technician'
      );
      const technicianNotEnabledCalendar = assosiatedTechs.find(
        (el) => !el?.calendarIntegrated
      );
      return data?.groupSize === 0 || technicianNotEnabledCalendar;
    }

    if (data?.type === 'technician') {
      return !data?.calendarIntegrated;
    }
  };

  const onSelectRecord = (dataArray) => {
    let updatedSelection = cloneDeep(dataArray);
    const selectedRecords = cloneDeep(records);
    const isDataSelected = updatedSelection.length > selectedRecords?.length;
    if (isDataSelected) {
      const addedItem = updatedSelection[updatedSelection?.length - 1];
      if (addedItem?.type === 'group') {
        const allMembers = cloneDeep(tableData);
        allMembers.forEach((el) => {
          if (el?.groupId === addedItem?.groupId && el?.type !== 'group') {
            updatedSelection.push(el);
          }
        });
      }
    } else {
      let { deletedItems } = getParsedCrudData(
        selectedRecords,
        updatedSelection,
        'id'
      );
      const deletedItem = deletedItems?.[0];
      if (deletedItem?.type === 'group') {
        updatedSelection = updatedSelection.filter(
          (el) => el?.groupId !== deletedItem?.groupId
        );
      }
    }
    setRecords(updatedSelection);
  };

  const defaultColumnCells = {
    TechnicianOrTechGroup: TechnicianOrTechGroupCell
  };

  const formatDataForTable = (data) => {
    const formattedData = [];
    data.forEach((user) => {
      const userData = {
        id: user?.userId,
        userId: user?.userId,
        name: user?.name,
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
        role: user?.roles?.map((item) => item?.name).join(''),
        type: 'technician',
        calendarIntegrated: user?.calendarIntegrated
      };
      formattedData.push(userData);
    });
    return formattedData;
  };

  const onPersonalisationComplete = (res) => {
    setPrimaryActionLoading(false);
  };

  const saveTechnicianToPersonalisation = () => {
    const technicianData = records.filter((el) => el?.type === 'technician');
    setPrimaryActionLoading(true);
    schedulingContext?.saveDispatcherTechniciansToPersonalisation(
      technicianData,
      onPersonalisationComplete
    );
  };

  const onCancel = () => {
    schedulingContext?.setDispatcherView(false);
  };

  const { loading: techDataLoading, data: techData } = useCachedQuery(
    GET_TECHS_BY_CRITERIA,
    {
      client: AccountClient,
      variables: {
        input: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      }
    }
  );

  useEffect(() => {
    const technicianData = techData?.getTechnicianList?.userList || [];
    if (!isEmpty(technicianData)) {
      const formattedData = formatDataForTable(technicianData);
      setTableData(formattedData);
    }
    fetchViewData();
  }, [techData]);

  const fetchViewData = () => {
    const cols = [
      {
        name: 'technicianOrTechGroup',
        label: getTranslatedText(intl, 'app.common.technician'),
        type: 'TechnicianOrTechGroup'
      },
      {
        name: 'email',
        label: getTranslatedText(intl, 'common.email.address')
      },
      {
        name: 'role',
        label: getTranslatedText(intl, 'client.users.role.tblHeader'),
        size: 100,
        minSize: 100,
        maxSize: 100
      }
    ].filter((item) => {
      if (productType === COMMON_CONSTANTS.IT && item.name === 'role') return;
      return item;
    });

    const columns = cols.map((col) => {
      return {
        Header: col.label,
        header: col.label,
        accessorKey: col.name,
        minSize: col?.size || 200,
        maxSize: col?.maxSize || null,
        size: col?.size || null,
        type: col?.type,
        id: col.name,
        key: col.name,
        disableResizing: true,
        className: 'sticky',
        disableSortBy: true,
        editable: false,
        disableFilters: true,
        field: col
      };
    });

    setColumnGroup([
      {
        Header: 'Name',
        header: 'Name',
        name: ' ',
        columns: [...columns]
      }
    ]);
  };

  return (
    <div className="max-w70vw">
      <Page
        title={getTranslatedText(
          intl,
          'scheduling.dispatcher.technicians.select.title'
        )}
        subtitle={getTranslatedText(
          intl,
          'scheduling.dispatcher.technicians.select.subtitle'
        )}
        contentHeight="155px"
        primaryAction={
          <ButtonGroup>
            <Button plainSecondary onClick={onCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={saveTechnicianToPersonalisation}
              disabled={records?.length === 0}
              loading={primaryActionLoading}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <div className="relative height-60vh">
          <SuperTable
            context={{
              records: records,
              setRecords: onSelectRecord,
              viewConditions: null
            }}
            data={tableData}
            columns={[...columnGroup]}
            defaultColumnCells={defaultColumnCells}
            //   emptyState={{
            //     nodataImage: `${envConfig.imageRoot}svg/Invoice.svg`,
            //     heading: getTranslatedText(
            //       intl,
            //       'admin.mandatoryTicketFields.list.noData.heading'
            //     ),
            //     description: getTranslatedText(
            //       intl,
            //       'admin.mandatoryTicketFields.list.noData.description'
            //     )
            //   }}
            listInfo={{ totalCount: tableData && tableData.length }}
            isLoading={techDataLoading}
            checkDisabledRow={isRowDisabled}
            refreshTable={fetchViewData}
            disableSelectAll
            maintainSelection
            idKey={'id'}
          />
        </div>
      </Page>
    </div>
  );
};

export default SelectTechnicianContainer;
